<template>
  <v-card elevation="0" class="pb-5 px-0">
    <v-card-text>
      <v-data-table
        :headers="tableHeaders"
        :items="items"
        :items-per-page="10"
        :loading="loading"
        loading-text="Loading Ingredients Selection... Please wait"
        hide-default-footer
        :header-props="{
          'sort-icon': 'mdi-unfold-more-horizontal',
        }"
        @pagination="paginationChanged()"
      >
        <template v-slot:top>
          <div class="d-flex mt-5">
            <v-text-field
              prepend-inner-icon="mdi-magnify"
              outlined
              height="5px"
              placeholder="Search"
              dense
              v-model="search"
              class="shrink"
            ></v-text-field>
            <v-btn
              :disabled="search.length < 1"
              color="primary"
              height="40"
              width="40"
              max-width="40"
              outlined
              class="ml-4"
              @click="searchIngredient()"
            >
              <v-icon>mdi-filter-outline</v-icon>
            </v-btn>
            <v-select
              v-model="currentItem"
              :items="selectItems"
              outlined
              rounded
              dense
              style="width: 100px"
              class="mx-4"
            ></v-select>
            <v-btn
              color="primary"
              height="40"
              depressed
              @click="$router.push({ name: 'AddNewIngredient' })"
            >
              {{ $t("ration.addIngredient") }}</v-btn
            >

            <v-spacer></v-spacer>
            <div class="py-3">{{ tableOptions.page }} of {{ totalPages }}</div>
            <v-btn
              class="mx-2"
              fab
              small
              outlined
              color="primary"
              :disabled="tableOptions.page <= 1"
              @click="pageChange(false)"
            >
              <v-icon dark> mdi-chevron-left </v-icon>
            </v-btn>
            <v-btn
              class="mx-2"
              fab
              small
              outlined
              color="primary"
              :disabled="tableOptions.page >= totalPages"
              @click="pageChange(true)"
            >
              <v-icon dark> mdi-chevron-right </v-icon>
            </v-btn>
            <v-menu
              :close-on-content-click="false"
              :nudge-width="200"
              left
              :min-width="417"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-2"
                  fab
                  small
                  outlined
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon dark> mdi-cog-outline </v-icon>
                </v-btn>
              </template>

              <v-card width="417">
                <v-list>
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>{{
                        $t("farm.customization")
                      }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider></v-divider>
                  <div class="pl-3 pt-2 font-weight-normal">
                    {{ $t("farm.recordsPerpage") }}
                  </div>
                  <v-list-item>
                    <v-list-item-content>
                      <v-slider
                        v-model="itemsPerPageSlider"
                        :tick-labels="itemsPerPageOptions"
                        :max="3"
                        step="1"
                        ticks="always"
                        tick-size="4"
                      ></v-slider>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider class="mt-n3"></v-divider>
                </v-list>
                <v-card-text>
                  <v-container fluid>
                    <v-row>
                      <v-col
                        class="mt-n9"
                        cols="6"
                        v-for="header in headers"
                        :key="header.value"
                      >
                        <v-checkbox
                          v-if="header.text != ''"
                          v-model="selectedHeaders"
                          :label="header.text"
                          color="primary"
                          :value="header.text"
                          hide-details
                          :disabled="
                            header.text == 'ID' ||
                            header.text == 'Tag' ||
                            header.text == ''
                          "
                        ></v-checkbox>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card-text>
                <v-layout justify-center>
                  <v-card-actions class="mb-5">
                    <v-spacer></v-spacer>

                    <v-btn outlined color="primary" width="190">
                      {{ $t("farm.restoreDefault") }}
                    </v-btn>
                    <v-btn color="primary">
                      {{ $t("farm.apply") }}
                    </v-btn>
                  </v-card-actions>
                </v-layout>
              </v-card>
            </v-menu>
          </div>
        </template>
        <template v-slot:progress>
          <v-progress-linear
            color="primary"
            :height="8"
            indeterminate
          ></v-progress-linear>
        </template>

        <template v-slot:item.checkbox="{ index, item }">
          <v-checkbox
            v-model="item.selected"
            @click="toggleCheckBox(item, index)"
          ></v-checkbox>
        </template>
        <template v-slot:item.quantity="{ index, item }">
          <div class="d-flex pt-6 quantity" v-if="item.isEdit">
            <v-text-field
              class="mr-2"
              v-model="item.quantity"
              type="number"
              outlined
              dense
              style="width: 30px"
              :disabled="!item.isEdit"
            ></v-text-field>
            <v-select
              :items="['kg']"
              outlined
              dense
              placeholder="Select"
              style="width: 20px"
              :disabled="!item.isEdit"
              v-model="item.maxQuantityUnit"
            ></v-select>
            <v-btn
              color="green"
              class="white--text ml-2"
              height="40"
              @click="isEditAction(item)"
              >Update</v-btn
            >
          </div>
          <div v-else>
            {{ item.quantity }} {{ item.maxQuantityUnit || "" }}
            <v-btn
              color="green"
              class="white--text ml-2"
              height="40"
              @click="isEditAction(item)"
              >Edit</v-btn
            >
          </div>
        </template>
        <template v-slot:item.ingredientPrice="{ item }">
          {{ item.currencyId }} {{ item.ingredientPrice }}
        </template>
        <template v-slot:item.stockVolume="{ item }">
          {{ item.stockVolume }} {{ item.unitAbbr }}
        </template>
        <template v-slot:item.stockStatus="{ item }">
          <v-btn
            v-if="item.stockStatus == 'In Stock'"
            x-small
            rounded
            min-width="120"
            color="green2"
            depressed
            class="primary--text border-green"
            height="34"
          >
            In Stock
          </v-btn>
          <v-btn
            v-else-if="item.stockStatus == 'Out Of Stock'"
            x-small
            rounded
            min-width="120"
            color="red2"
            depressed
            class="red--text border-red"
            height="34"
          >
            Out Of Stock
          </v-btn>
          <v-btn
            v-else-if="item.stockStatus == 'Critical'"
            x-small
            rounded
            min-width="120"
            color="#FFF0D9"
            depressed
            class="orange--text border-yellow"
            height="34"
          >
            Critical
          </v-btn>
          <v-btn
            v-else-if="item.stockStatus == 'Low Stock'"
            x-small
            rounded
            min-width="120"
            color="#E8E8E8"
            depressed
            class="black--text border-black"
            height="34"
          >
            Low Stock
          </v-btn>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>
<script>
import { getUserIngredientDetail } from "@/graphql/Feed.gql";
import { mapGetters } from "vuex";
import Vue from "vue";
export default {
  computed: {
    ...mapGetters(["groupDetailForRationSuggestion"]),
  },
  mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },
  watch: {
    /* eslint-disable */
    updateTab(nV, oV) {
      this.activeTab = this.tab;
      this.loadTabContent();
    },
  },
  data() {
    return {
      subGroups: [
        { id: 1, name: "Sub Group 1" },
        { id: 2, name: "Sub Group 2" },
      ],
      totalRecords: 0,
      totalPages: 0,
      ingredients: [],
      currentItem: "All",
      selectItems: ["All", "One"],
      currentUnit: "Kg",
      units: ["Kg", "g"],
      menu: {},
      itemsPerPageSlider: 0,
      itemsPerPageOptions: [5, 10, 25, 50],
      to: "",
      from: "",
      search: "",
      selected: [],
      totalAnimals: 1,
      selectedItem: false,
      loading: false,
      length: 0,
      pageCount: 1,
      tableOptions: { page: 1, limit: 5 },
      loadingText: "Loading... Please wait",
      tab: 0,
      headers: [
        {
          text: "",
          align: "start",
          value: "checkbox",
          sortable: false,
          class: "black--text",
        },
        {
          text: this.$t("ration.feedIngredients"),
          value: "ingredientName",
          class: "black--text",
        },
        {
          text: this.$t("ration.costPerUnit"),
          value: "ingredientPrice",
          class: "black--text",
        },
        {
          text: this.$t("ration.stockVolume"),
          value: "stockVolume",
          class: "black--text",
        },
        {
          text: this.$t("ration.maxQuantity"),
          value: "quantity",
          class: "black--text",
        },
        {
          text: this.$t("ration.expiryDate"),
          value: "expiryDate",
          class: "black--text",
        },
        {
          text: this.$t("ration.stockStatus"),
          sortable: false,
          value: "stockStatus",
          class: "black--text",
        },
      ],

      selectedHeaders: [],
      allSelectedHeaders: [
        this.$t("ration.feedIngredients"),
        this.$t("ration.costPerUnit"),
        this.$t("ration.stockVolume"),
        this.$t("ration.maxQuantity"),
        this.$t("ration.expiryDate"),
        this.$t("ration.stockStatus"),
      ],
      tableHeaders: [],
      items: [],
      currentSubGroup: null,
    };
  },
  methods: {
    searchIngredient() {
      if (this.search == "") return;

      if (this.checkBreakPoints) {
        this.tableOptions.page = 1;
        // this.getTabletAnimalsData();
      } else {
        this.tableOptions.page = 1;
        // this.getAnimalsData();
      }
      this.loadTabContent();
      // this.$apollo.queries.getUserIngredientDetail.refetch();
    },

    markCheckedData(items) {
      const groupIndex = this.groupDetailForRationSuggestion.findIndex(
        obj => obj.groupId == this.groupDetails.groupId
      );
      const subGroupIndex = this.groupDetailForRationSuggestion[
        groupIndex
      ]?.subGroup.findIndex(subgroup => subgroup.id == this.currentSubGroup.id);

      let itemsWithChecked = [];
      for (let i = 0; i < items.length; i++) {
        if (
          this.groupDetailForRationSuggestion[groupIndex]?.subGroup[
            subGroupIndex
          ]?.ingredients &&
          this.groupDetailForRationSuggestion[groupIndex]?.subGroup[
            subGroupIndex
          ]?.ingredients.findIndex(
            ingredient => ingredient.ingredientID == items[i].ingredientID
          ) != -1
        ) {
          itemsWithChecked.push({ ...items[i], selected: true });
        } else {
          itemsWithChecked.push({ ...items[i], selected: false });
        }
      }
      console.log(itemsWithChecked);
      return itemsWithChecked;
    },

    toggleCheckBox(item, subGroup) {
      const dataForStore = {
        groupId: this.groupDetails?.groupId,
        subGroupId: subGroup.id,
        ingredients: item,
      };
      if (!item.selected) {
        this.$store.commit("removeIngredientsForAllSubGroups", dataForStore);
      } else {
        this.$store.commit("selectIngredientsForAllSubGroups", dataForStore);
      }
    },
    isEditAction(item) {
      const index = this.items.findIndex(
        i => i.ingredientID === item.ingredientID
      );
      Vue.set(this.items[index], "isEdit", !this.items[index].isEdit);
      Vue.set(
        this.items[index],
        "maxQuantityUnit",
        this.items[index]?.maxQuantityUnit || "kg"
      );
    },

    pageChange(t) {
      if (t) this.tableOptions.page = Number(this.tableOptions.page) + 1;
      else this.tableOptions.page = Number(this.tableOptions.page) - 1;
      this.page = this.tableOptions.page;
      // this.$router.replace({ query: { page: this.tableOptions.page } });
      this.loadTabContent();
    },
    paginationChanged() {
      this.page = this.tableOptions.page;
      // this.$router.replace({ query: { page: this.tableOptions.page } });
      this.loadTabContent();
    },

    activateTab(subGroup) {
      this.currentSubGroup = subGroup;
      this.$emit("selectedSubGroupTabChange", subGroup.id);
      this.activeTab = this.tab;
      this.loadTabContent();
    },

    async loadTabContent() {
      this.loading = true;
      this.currentSubGroup =
        this.currentSubGroup ?? this.groupDetails?.subGroup[0];
      // const tab = this.tabs[index];
      // if (!tab.contentLoaded) {
      // Make the GraphQL query to fetch the content for the selected tab
      const response = await this.$apollo.query({
        query: getUserIngredientDetail,
        fetchPolicy: "no-cache",
        variables: {
          ingredientListInput: {
            page: this.tableOptions.page,
            limit: this.tableOptions.limit,
            searchPhrase: this.search,
          },
        },
      });

      this.items = this.markCheckedData(
        response.data.getUserIngredientDetail.rows
      );
      this.totalRecords = response.data.getUserIngredientDetail.totalRecords;
      this.totalPages = response.data.getUserIngredientDetail.totalPages;
      this.loading = false;
    },

    resetTableStructure(index) {
      // this.startLoading();
      this.tableHeaders = this.headers.filter(header =>
        this.selectedHeaders.includes(header.text)
      );
      this.tableOptions.limit =
        this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.menu[index] = false;
      this.loadTabContent();
      // this.stopLoading();
    },

    resetDefault(index) {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu[index] = false;
      this.loadTabContent();
    },
  },
  props: {
    groupDetails: {
      type: Object,
      required: true,
    },
    updateTab: {
      type: Number,
    },
  },
};
</script>

<style scoped>
.border-green {
  border: 1px solid green !important;
}

.border-red {
  border: 1px solid red !important;
}

.border-black {
  border: 1px solid #000000 !important;
}

.border-yellow {
  border: 1px solid #ffb443 !important;
}

.quantity {
  min-width: 200px !important;
}
.py-3 {
  width: 9%;
}
</style>
